/**
 * @fileoverview Journal d'audit backoffice.
 *
 * Toutes les actions sensibles doivent être consultables par capacité `audit:read`.
 * Le tableau reste une vue de consultation, le détail technique s'ouvre dans
 * un `Sheet` pour ne pas surcharger la page.
 */

import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Amount,
  Badge,
  Button,
  DataCard,
  DataTable,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  SearchInput,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@gnpc/ui';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { AUDIT_LOG, formatDateTime, type AuditEntry } from '../data/backoffice.ts';
import { CapabilityGuard } from '../features/auth/CapabilityProvider.tsx';

const ACTION_LABELS: Record<string, string> = {
  'payment.replay.requested': "Relance d'enregistrement Quipux",
  'refund.requested': 'Demande de remboursement',
  'refund.approved': 'Approbation de remboursement',
  'reconciliation.discrepancy.closed': "Clôture d'écart de rapprochement",
  'integration.health.changed': "Santé d'intégration modifiée",
};

const TARGET_LABELS: Record<string, string> = {
  payment: 'Paiement',
  refund: 'Remboursement',
  reconciliation: 'Rapprochement',
  integration: 'Intégration',
};

const columns: ColumnDef<AuditEntry>[] = [
  { accessorKey: 'actorEmail', header: 'Acteur' },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ row }) => getAuditActionLabel(row.original),
  },
  {
    accessorKey: 'capability',
    header: 'Capacité',
    cell: ({ row }) => (
      <Badge variant="outline" className="bg-background">
        {row.original.capability ?? 'Système'}
      </Badge>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'Montant',
    cell: ({ row }) =>
      typeof row.original.amount === 'number' ? (
        <Amount value={row.original.amount} />
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  { accessorKey: 'ipAddress', header: 'IP' },
  { accessorKey: 'createdAt', header: 'Date', cell: ({ row }) => formatDateTime(row.original.createdAt) },
];

function getAuditActionLabel(entry: AuditEntry): string {
  return ACTION_LABELS[entry.action] ?? entry.action;
}

function getAuditTargetLabel(entry: AuditEntry): string {
  return `${TARGET_LABELS[entry.targetType] ?? entry.targetType} · ${entry.targetId}`;
}

export function AuditLogPage(): JSX.Element {
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  /** Filtre par période sur la date de l'événement (C-29). */
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const selectedAudit = useMemo(
    () => AUDIT_LOG.find((entry) => entry.id === selectedAuditId) ?? null,
    [selectedAuditId],
  );

  /**
   * Filtrage local insensible à la casse sur les champs identifiants d'une
   * entrée : acteur, code action, type et identifiant de cible. Le libellé
   * humain de l'action reste consultable via le code action brut.
   */
  const filteredEntries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return AUDIT_LOG.filter((entry) => {
      const matchesQuery =
        !normalizedQuery ||
        [entry.actorEmail, entry.action, entry.targetType, entry.targetId].some((field) =>
          field.toLowerCase().includes(normalizedQuery),
        );
      const entryDate = entry.createdAt.slice(0, 10);
      const matchesDate =
        (!dateFrom || entryDate >= dateFrom) && (!dateTo || entryDate <= dateTo);
      return matchesQuery && matchesDate;
    });
  }, [query, dateFrom, dateTo]);

  if (AUDIT_LOG.length === 0) {
    return (
      <EmptyState
        title="Aucune entrée d'audit"
        description="Les actions sensibles apparaîtront ici après journalisation."
      />
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Audit"
        title="Journal d'audit immuable"
        description="Tracer qui a fait quoi, quand, avec quelle capacité et sur quelle cible."
        actions={
          <CapabilityGuard capability="audit:export">
            <Button
              variant="outline"
              onClick={() =>
                toast.success('Export du journal lancé', {
                  description: 'Le fichier sera disponible dans Exports.',
                })
              }
            >
              <Download className="size-4" />
              Exporter
            </Button>
          </CapabilityGuard>
        }
      />

      <section className="grid gap-6">
        <DataTable
          columns={columns}
          data={filteredEntries}
          rowAction={{
            label: (entry) => `Ouvrir le détail de l'événement ${getAuditActionLabel(entry)}`,
            onSelect: (entry) => {
              setSelectedAuditId(entry.id);
              setDetailSheetOpen(true);
            },
          }}
          toolbar={
            <div className="grid w-full items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="lg:col-span-2">
                <SearchInput
                  size="sm"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onClear={() => setQuery('')}
                  placeholder="Acteur, action, cible…"
                />
              </div>
              <FormField label="Date de début">
                <Input
                  size="sm"
                  type="date"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </FormField>
              <FormField label="Date de fin">
                <Input
                  size="sm"
                  type="date"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </FormField>
            </div>
          }
          mobileCardLabel="Entrées d'audit"
          mobileCard={(entry) => (
            <DataCard
              eyebrow={entry.actorEmail}
              title={getAuditActionLabel(entry)}
              description={getAuditTargetLabel(entry)}
              aside={<code className="text-xs">{entry.ipAddress}</code>}
              meta={
                <>
                  <span className="text-muted-foreground">{entry.capability ?? 'Système'}</span>
                  <span className="text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
                </>
              }
            />
          )}
          emptyState={<EmptyState title="Aucune entrée" description="Le journal affichera les actions sensibles." />}
          pagination={{ label: `${filteredEntries.length} entrée(s)`, hasNextPage: false }}
        />
        {/* Conservation / rétention du journal (C-29). */}
        <p className="text-muted-foreground text-xs">
          Journal immuable — toutes les actions sensibles et financières (relance, approbation de
          remboursement, clôture de rapprochement) sont tracées avec leur montant et conservées
          10 ans.
        </p>
      </section>

      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Détail de l'événement</SheetTitle>
            <SheetDescription>
              {selectedAudit ? getAuditActionLabel(selectedAudit) : "Entrée d'audit"}
            </SheetDescription>
          </SheetHeader>

          {selectedAudit ? (
            <div className="grid gap-4 p-5">
              <div className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase">Action</p>
                    <p className="text-quipux-navy-950 mt-1 font-medium">
                      {getAuditActionLabel(selectedAudit)}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-background">
                    {selectedAudit.capability ?? 'Système'}
                  </Badge>
                </div>
              </div>

              <dl className="grid gap-3 rounded-lg border border-border p-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Acteur</dt>
                  <dd className="mt-1 font-medium">{selectedAudit.actorEmail}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Cible</dt>
                  <dd className="mt-1 font-medium">{getAuditTargetLabel(selectedAudit)}</dd>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">Adresse IP</dt>
                    <dd className="mt-1 font-medium">{selectedAudit.ipAddress}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Date</dt>
                    <dd className="mt-1">{formatDateTime(selectedAudit.createdAt)}</dd>
                  </div>
                </div>
                {typeof selectedAudit.amount === 'number' ? (
                  <div>
                    <dt className="text-muted-foreground">Montant</dt>
                    <dd className="mt-1 font-medium">
                      <Amount value={selectedAudit.amount} />
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-muted-foreground">Code action</dt>
                  <dd className="mt-1 font-mono text-xs">{selectedAudit.action}</dd>
                </div>
              </dl>

              <div className="grid gap-2 rounded-lg border border-border p-4 text-sm">
                <p className="text-quipux-navy-950 font-medium">Données de l'événement</p>
                <pre className="bg-quipux-navy-950 overflow-auto rounded-lg p-4 text-xs leading-relaxed text-white">
                  {JSON.stringify(selectedAudit.changes, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}

          <SheetFooter className="border-border border-t p-5">
            <SheetClose asChild>
              <Button variant="outline">Fermer</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
